import moment from 'moment';
import React, { useEffect } from 'react';
import config from 'Config';
import { useAppContext } from 'Context/ReactAppContext';
import { useDebounce } from 'Src/hooks/use-debounce/useDebounce';
import { useScriptLoader } from 'Src/hooks/use-script-loader/useScriptLoader';
const supportedLang = [
  'de',
  'en',
  'es',
  'fr',
  'it',
  'ja-JP',
  'ko',
  'pt-BR',
  'pt-PT',
  'nl',
  'ru',
  'sv-SE',
  'tr',
  'zh',
];

const getLang = ({ locale, language }: { locale: string; language: string }) => {
  if (locale === 'se-SE') {
    return 'sv-SE';
  }

  if (supportedLang.includes(locale)) {
    return locale;
  }

  if (supportedLang.includes(language)) {
    return language;
  }

  return 'en';
};

const travelSherpaScriptUrl = String(config.get('travelSherpa.scriptUrl') ?? '');

const formatDateforTravelSherpa = (date: number | moment$Moment) =>
  moment(date).format('YYYY-MM-DD');

const elementConfig = ({ departureDate, lang, product, returnDate, withInboundOutbound }) => ({
  language: lang,
  placement: 'mmb',
  ...(product && {
    travellers: [
      {
        nationality: product.travelSherpaOutbound,
        vaccinations: [
          {
            type: 'COVID_19',
            status: 'FULLY_VACCINATED',
          },
        ],
      },
    ],
    ...(withInboundOutbound &&
      product.travelSherpaOutbound !== product.travelSherpaInbound && {
        segments: [
          {
            segmentType: 'OUTBOUND',
            origin: {
              countryCode: product.travelSherpaOutbound,
            },
            destination: {
              countryCode: product.travelSherpaInbound,
            },
            ...(departureDate && {
              departureDate: formatDateforTravelSherpa(Number(departureDate)),
            }),
          },
          {
            segmentType: 'RETURN',
            origin: {
              countryCode: product.travelSherpaInbound,
            },
            destination: {
              countryCode: product.travelSherpaOutbound,
            },
            ...(returnDate && {
              arrivalDate: returnDate,
            }),
            ...(returnDate && {
              departureDate: returnDate,
            }),
          },
        ],
      }),
  }),
});

type Props = {
  className?: string;
  disclaimer: string;
  isLoadingWidget?: boolean;
  product?: Product;
  withInboundOutbound?: boolean;
  departureDate: number;
  duration: number;
};
export const TravelSherpaWidget = ({
  className,
  disclaimer,
  isLoadingWidget,
  product,
  withInboundOutbound = true,
  departureDate,
  duration,
}: Props) => {
  useScriptLoader(String(travelSherpaScriptUrl));
  const { language, locale } = useAppContext();
  const returnDate =
    departureDate &&
    duration &&
    formatDateforTravelSherpa(moment(String(departureDate)).add(duration, 'days'));
  const debouncedCriteria = useDebounce([departureDate, duration], 500);
  const [dateCriteria, durationCriteria] = debouncedCriteria;
  const hasDebouncedCriteria = Boolean(isLoadingWidget && dateCriteria && durationCriteria);
  const lang = getLang({
    language: String(language),
    locale: String(locale),
  });
  useEffect(
    () => {
      const travelSherpaconfig = elementConfig({
        departureDate,
        lang,
        product,
        returnDate,
        withInboundOutbound,
      });

      if (isLoadingWidget) {
        if (typeof window.$sherpa !== 'undefined') {
          window.$sherpa?.V2?.createElement('trip', travelSherpaconfig)?.mount(
            '#sherpa-trip-element',
          );
        } else {
          window.onSherpaEvent = (event: { type?: string }) => {
            if (event.type === 'sdkLoaded') {
              // sdk is ready, create and mount new elements
              window.$sherpa?.V2?.createElement('trip', travelSherpaconfig)?.mount(
                '#sherpa-trip-element',
              );
            }
          };
        }
      }
    },
    withInboundOutbound && product && product.travelSherpaOutbound !== product.travelSherpaInbound
      ? [isLoadingWidget, hasDebouncedCriteria]
      : [],
  );
  return (
    <div className={className}>
      <div id="sherpa-trip-element" />
      {!!disclaimer && <p className="font-sans text-10">{disclaimer}</p>}
    </div>
  );
};
